import { useEffect, useRef, useState } from 'react';
import { DISPLAY_NAME_MAX_LENGTH } from '@ni/shared';
import { useMayWrite } from '../../api/app-context';
import { axisOfWrite } from '../../api/client';
import { useRenameKnowledge } from '../../api/hooks';
import { errorLine, isNotFound, nameTakenSentence, validationSentence } from '../../api/errors';
import { Modal } from '../ui/modal';
import { WorkingButton } from '../ui/working-button';
import {
  ACCESS_CLOSE,
  ACCESS_OWNER_ONLY,
  CANCEL,
  KNOWLEDGE_RENAME_GONE,
  KNOWLEDGE_RENAME_HELP,
  KNOWLEDGE_RENAME_LABEL,
  KNOWLEDGE_RENAME_SAME,
  KNOWLEDGE_RENAME_SAVE,
  KNOWLEDGE_RENAME_TITLE,
  KNOWLEDGE_RENAME_WORKING,
  knowledgeRenameLead,
} from '../../lib/copy';
import { Button } from '../ui/button';
import { Field } from '../ui/field';

/**
 * **Rename this record** (organisations-as-subjects note §2 chunk 1) — one field,
 * and the only control in the product that changes what a record is called
 * without folding it into another one.
 *
 * The field is **pre-filled with the current name**, which is what makes this a
 * correction rather than a fresh entry: the names it exists for are almost right
 * ("Medici's Cancer Shield Program" → "Medici", "T Dupree Scovell" → "T. Dupree
 * Scovell"), and asking a reader to retype one they can see in front of them is
 * how a typo gets filed as a rename.
 *
 * The consequence of that is the disabled state. The modal **opens** with the
 * button unpressable, because the field already holds the record's name — which
 * is why the line beside it says what is true ("That is already the name.")
 * rather than complaining about something the reader has not yet done.
 *
 * The view is split from the writer the way `LinkRecordModalView` is, and for
 * the same reason: this package's suite has no DOM, so what is on the page is
 * asserted against the view's markup.
 */

/**
 * **The door a member cannot open, saying so** — one shape for all three writes
 * that have one (`ACCESS_OWNER_ONLY`).
 *
 * It is the *write's* refusal and not the entry point's, which is what lets the
 * entry points multiply safely: a pasted link and a remembered menu item reach
 * the same sentence as the control that was drawn for it.
 */
export function AccessRefusedModal({ label, onClose }: { label: string; onClose: () => void }) {
  return (
    <Modal label={label} onClose={onClose}>
      <div className="modal-head">{label}</div>
      <div className="modal-line">{ACCESS_OWNER_ONLY}</div>
      <div className="modal-actions">
        <Button variant="act" onClick={onClose}>
          {ACCESS_CLOSE}
        </Button>
      </div>
    </Modal>
  );
}

export function RenameRecordModalView({
  mayWrite,
  currentName,
  typed,
  working,
  error,
  renamed,
  onTyped,
  onSave,
  onClose,
}: {
  /**
   * Whether this reader may rename a record at all. **The gate is here**, on the
   * write, rather than on the control that opens this modal: `false` renders the
   * sentence and a way out, and no field to type a name nobody will store into.
   */
  mayWrite: boolean;
  /** What the record is called now — the sub-line, and what "unchanged" means. */
  currentName: string;
  typed: string;
  working: boolean;
  error: unknown;
  /** Set once the rename has landed: the server's own sentence about it. */
  renamed: string | null;
  onTyped: (next: string) => void;
  onSave: () => void;
  onClose: () => void;
}) {
  /* Trimmed on both sides of the comparison, because the server trims before it
     decides too: a trailing space is not a rename, and offering to send one
     would be offering a press whose answer is "that is already the name". */
  const next = typed.trim();
  const same = next === currentName.trim();

  /* **Three refusals this door reads for itself**, and everything else falls
     through to the generic line.

     `NAME_TAKEN` is the one that matters: it is a sentence the server wrote
     naming the name that is taken, and the reader's next move is to type a
     different one in the field they are already standing in. A 404 is the record
     having been merged or removed between the panel being opened and the button
     being pressed, and the envelope's bare "Not found" is a fragment rather than
     an instruction. `VALIDATION_FAILED` covers the ceiling on length. */
  const line =
    nameTakenSentence(error) ??
    (isNotFound(error) ? KNOWLEDGE_RENAME_GONE : (validationSentence(error) ?? errorLine(error)));

  if (!mayWrite) return <AccessRefusedModal label={KNOWLEDGE_RENAME_TITLE} onClose={onClose} />;

  return (
    <Modal label={KNOWLEDGE_RENAME_TITLE} onClose={onClose}>
      <div className="modal-head">{KNOWLEDGE_RENAME_TITLE}</div>
      <div className="modal-line">{knowledgeRenameLead(currentName)}</div>

      <Field label={KNOWLEDGE_RENAME_LABEL} htmlFor="knowledge-rename-name">
        {/* No browser autocomplete: the one useful value is already in the box,
            and a dropdown of names typed into other forms has nothing to add.

            The ceiling is the contract's, said here as well as there: a paste
            into the wrong box is what the limit exists for, and stopping it at
            the field is kinder than a 400 the reader has to read. */}
        <input
          id="knowledge-rename-name"
          type="text"
          autoComplete="off"
          maxLength={DISPLAY_NAME_MAX_LENGTH}
          value={typed}
          disabled={working}
          onChange={(event) => onTyped(event.target.value)}
        />
        {/* The half of a rename nobody can see, said where the decision is made
            rather than in a confirmation after it. */}
        <div className="field-note">{KNOWLEDGE_RENAME_HELP}</div>
      </Field>

      {/* What the record now says, in the server's own words — it is the answer
          that knows whether a redirect was written, so it is the answer that
          says so. Shown where the reader is looking; the writer closes the modal
          a beat later. */}
      {renamed ? (
        <div className="link-done" aria-live="polite">
          <div className="src">{renamed}</div>
        </div>
      ) : null}

      {error ? <div className="src">{line}</div> : null}

      <div className="modal-actions">
        <WorkingButton
          label={KNOWLEDGE_RENAME_SAVE}
          workingLabel={KNOWLEDGE_RENAME_WORKING}
          working={working}
          disabled={same || next.length === 0}
          onClick={onSave}
        />
        {/* Said beside the disabled button rather than as an error: nothing has
            gone wrong, and this is the state the modal opens in. */}
        {same ? <span className="src">{KNOWLEDGE_RENAME_SAME}</span> : null}
        {/* Never disabled while the write runs, as every other confirm in the
            app: walking away is the reader's to do. */}
        <Button variant="act" onClick={onClose}>
          {CANCEL}
        </Button>
      </div>
    </Modal>
  );
}

/** How long the sentence stands before the modal closes itself. */
const CLOSE_AFTER_MS = 900;

/**
 * The writer: one field and one mutation.
 *
 * On success the sentence is shown and *then* the modal closes, rather than
 * closing on the response: the second clause of that sentence — "The old name
 * still finds it." — is the only place the redirect is ever mentioned, and a
 * modal that vanished on the press would never have said it.
 */
export function RenameRecordModal({
  here,
  onClose,
}: {
  here: { entity_id: string; display_name: string };
  onClose: () => void;
}) {
  const [typed, setTyped] = useState(here.display_name);
  const [renamed, setRenamed] = useState<string | null>(null);
  const write = useRenameKnowledge(here.entity_id);
  const mayWrite = useMayWrite(axisOfWrite('renameKnowledge'));

  /* The closing timer, cancelled if the reader gets there first: a modal closed
     by hand inside the beat the sentence stands for must not be closed a second
     time by a timer nobody can see. */
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(closeTimer.current), []);

  return (
    <RenameRecordModalView
      mayWrite={mayWrite}
      currentName={here.display_name}
      typed={typed}
      working={write.isPending}
      error={write.error}
      renamed={renamed}
      onTyped={(next) => {
        if (write.isError) write.reset();
        setRenamed(null);
        setTyped(next);
      }}
      onSave={() => {
        if (write.isPending) return;
        write.mutate(
          { name: typed.trim() },
          {
            onSuccess: (response) => {
              /* No success callback out of here, and neither door wants one:
                 `useRenameKnowledge` drops the knowledge, record and network
                 scopes on settle, so the panel head and the record header behind
                 this modal are already saying the new name when it closes. */
              setRenamed(response.sentence);
              closeTimer.current = setTimeout(onClose, CLOSE_AFTER_MS);
            },
          },
        );
      }}
      onClose={onClose}
    />
  );
}
